/**
 * AdminLayout — Modern admin layout with server-driven sidebar navigation.
 *
 * iter-438k: retires the hardcoded `AdminSidebar` MENU_SECTIONS in favour
 * of `<DynamicSidebar workspaceKey="events" />`, which fetches its menu
 * tree from `/api/identity/workspace/events/nav`. The layout still owns
 * `activeTab`/`onTabChange`, so tab-driven items dispatch to `AdminEventPage`'s
 * switch while route-driven items (Schools, Bookshop, etc.) navigate directly.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/lib/store';
import { DynamicSidebar } from '@/components/DynamicSidebar';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import NotificationBell from '@/components/admin/NotificationBell';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Menu, X, Home, LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';
import api from '@/lib/api';

const AdminLayout = ({ children, event, activeTab, onTabChange }) => {
  const navigate = useNavigate();
  const { logout, admin, setAuth, token, user, userType, eventId } = useAuthStore();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Iter 313 — hydrate permissions from backend so DynamicSidebar sees
  // the caller's real menu scope on first paint.
  useQuery({
    queryKey: ['rbac-my-menu'],
    queryFn: async () => {
      const r = await api.get('/admin/rbac/my-menu');
      return r.data;
    },
    onSuccess: (d) => {
      const perms = d?.flat_permissions || [];
      if (perms.length > 0) {
        setAuth(token, user, userType, eventId, perms);
      }
    },
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Pull pending-tribute counts so we can badge the moderation menu entry.
  const { data: pendingCounts } = useQuery({
    queryKey: ['admin-pending-counts'],
    queryFn: () => api.get('/sites/admin-aggregate/pending-counts').then((r) => r.data).catch(() => ({ total_pending: 0 })),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const badgeValues = { tribute_pending: pendingCounts?.total_pending || 0 };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavigate = (item) => {
    // Tab-driven → dispatch to AdminEventPage switch. Route-driven items
    // are handled by DynamicSidebar itself (React Router push).
    if (item.tab_id) {
      onTabChange(item.tab_id);
      setMobileMenuOpen(false);
    } else if (item.route) {
      setMobileMenuOpen(false);
    }
  };

  const renderFooter = ({ collapsed, variant }) => (
    <div className={cn(
      'p-3 border-t',
      variant === 'dark' ? 'border-slate-800' : 'border-slate-200',
    )}>
      {!collapsed ? (
        <div className="space-y-2">
          <div className={cn(
            'flex items-center gap-3 p-2 rounded-lg',
            variant === 'dark' ? 'bg-slate-800' : 'bg-slate-100',
          )}>
            <Avatar className="h-9 w-9">
              <AvatarImage src={admin?.avatar_url} />
              <AvatarFallback className="bg-indigo-600 text-white text-sm">
                {admin?.first_name?.[0]}{admin?.last_name?.[0]}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className={cn(
                'text-sm font-medium truncate',
                variant === 'dark' ? 'text-white' : 'text-slate-900',
              )}>
                {admin?.first_name} {admin?.last_name}
              </p>
              <p className={cn(
                'text-xs truncate',
                variant === 'dark' ? 'text-slate-400' : 'text-slate-500',
              )}>
                {admin?.email}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                'flex-1 justify-start',
                variant === 'dark'
                  ? 'text-slate-400 hover:text-amber-400 hover:bg-slate-800'
                  : 'text-slate-500 hover:text-amber-600 hover:bg-amber-50',
              )}
              onClick={() => navigate('/admin')}
              data-testid="exit-event-btn"
            >
              <Home className="h-4 w-4 mr-2" />
              Exit Event
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                'flex-1 justify-start',
                variant === 'dark'
                  ? 'text-slate-400 hover:text-red-400 hover:bg-slate-800'
                  : 'text-slate-500 hover:text-red-600 hover:bg-red-50',
              )}
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-1">
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              'w-full',
              variant === 'dark' ? 'text-slate-400 hover:text-amber-400' : 'text-slate-500 hover:text-amber-600',
            )}
            onClick={() => navigate('/admin')}
            data-testid="exit-event-btn-collapsed"
          >
            <Home className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              'w-full',
              variant === 'dark' ? 'text-slate-400 hover:text-red-400' : 'text-slate-500 hover:text-red-600',
            )}
            onClick={handleLogout}
            data-testid="logout-btn-collapsed"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex h-screen bg-slate-100 overflow-hidden">
      {/* Desktop Sidebar */}
      <div className="hidden lg:block h-full">
        <DynamicSidebar
          workspaceKey="events"
          variant="dark"
          activeTab={activeTab}
          onNavigate={handleNavigate}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          renderFooter={renderFooter}
          badgeValues={badgeValues}
        />
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <div className={cn(
        "fixed inset-y-0 left-0 z-50 lg:hidden transition-transform duration-300",
        mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <DynamicSidebar
          workspaceKey="events"
          variant="dark"
          activeTab={activeTab}
          onNavigate={handleNavigate}
          collapsed={false}
          onToggleCollapse={() => setMobileMenuOpen(false)}
          renderFooter={renderFooter}
          badgeValues={badgeValues}
        />
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Header Bar */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6 flex-shrink-0">
          <div className="flex items-center gap-4">
            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>

            {/* Breadcrumb / Page Title */}
            <div>
              <h1 className="text-lg font-semibold text-slate-900">
                {event?.name || 'Dashboard'}
              </h1>
              <p className="text-sm text-slate-500 hidden sm:block">
                {getTabTitle(activeTab)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Language switcher */}
            <LanguageSwitcher variant="ghost" showLabel={false} />

            {/* Notifications */}
            <NotificationBell />

            {/* User Info (mobile) */}
            <div className="lg:hidden flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-indigo-600 flex items-center justify-center text-white text-sm font-medium">
                {admin?.first_name?.[0]}{admin?.last_name?.[0]}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

// Helper function to get tab titles
function getTabTitle(tab) {
  const titles = {
    overview: 'Event Overview',
    analytics: 'Analytics & Reports',
    delegates: 'Delegate Management',
    checkin: 'Check-in Operations',
    kiosk: 'Kiosk Mode',
    branding: 'Event Branding',
    sessions: 'Session Management',
    speakers: 'Speaker Management',
    stage: 'Virtual Stage',
    livestream: 'Livestream',
    realtime: 'Live Dashboard',
    'qr-health': 'QR Health Check',
    communications: 'Communications Hub',
    tasks: 'Task Board',
    scavenger: 'Scavenger Hunt',
    economy: 'Event Economy',
    housing: 'Housing Management',
    venue: 'Venue Maps',
    safety: 'Safety Center',
    'campus-access': 'Campus Access',
    sponsors: 'Sponsor Management',
    booths: 'Exhibitor Booths',
    surveys: 'Survey Management',
    certificates: 'Certificates',
    team: 'Team Management',
    roles: 'Roles & Permissions',
    audit: 'Audit Log',
    research: 'AI Research Tool',
    settings: 'Event Settings',
  };
  return titles[tab] || 'Dashboard';
}

export default AdminLayout;
